import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { LinkField, LinkType } from '@prismicio/client';
import { SelfcareService } from '@yol-digital/ms-client';
import { catchError, EMPTY, map } from 'rxjs';
import { ENVIRONMENT_URLS_CONFIG_TOKEN } from 'app-initialization';
import { AuthService, UserService } from 'auth-data-access';
import { BrandService } from 'brand';
import { SvgComponent } from 'icon';
import { LanguageService } from 'language';
import { LinkComponent } from 'link';
import { MenuComponent, MenuItemComponent } from 'menu';
import { RedirectService } from 'redirect';
import { ToastService } from 'toast';
import { TranslatePipe, TranslateService } from 'translate';
import { BrowserService } from 'utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-dropdown-menu',
  imports: [MenuItemComponent, MenuComponent, SvgComponent, LinkComponent, TranslatePipe],
  templateUrl: './dropdown-menu.component.html',
})
export class DropdownMenuComponent {
  private breakpointObserver = inject(BreakpointObserver);
  private user = inject(UserService);
  private authService = inject(AuthService);
  private lang = inject(LanguageService);
  public brandService = inject(BrandService);
  private translateService = inject(TranslateService);
  private toastService = inject(ToastService);
  private browserService = inject(BrowserService);
  public languageService = inject(LanguageService);
  public config = inject(ENVIRONMENT_URLS_CONFIG_TOKEN);
  public account = signal<SelfcareService.AccountResp>(null);
  public collapseOnMobile = input(false);
  public lightTheme = input(false);
  public isSmallScreenSize = toSignal(
    this.breakpointObserver.observe('(min-width: 1024px)').pipe(map(({ matches }) => !matches))
  );
  public redirectService = inject(RedirectService);
  public accountLabel = computed(() => {
    this.translateService.translationCompleteSignal();
    return this.isSmallScreenSize()
      ? this.account()?.ownerDetails?.firstName.charAt(0).toUpperCase() +
          this.account()?.ownerDetails?.lastName.charAt(0).toUpperCase()
      : this.translateService.getTranslation(['login', 'my_account']);
  });

  constructor() {
    this.user.getAccount().subscribe(account => {
      this.account.set(account);
    });
  }

  public logout(): void {
    this.authService
      .logout()
      .pipe(
        catchError(err => {
          if (!err?.wasCaught) {
            console.error(err);
            this.toastService.add(this.translateService.getTranslation(['login', 'error', 'generic']), false);
          }
          return EMPTY;
        })
      )
      .subscribe(() => {
        this.account.set(null);
      });
  }

  public loginLink = computed<LinkField>(() => {
    if (!this.config.websiteUrl || this.config.websiteUrl === this.browserService.getOrigin()) {
      return <LinkField>{ link_type: LinkType.Document, slug: `auth/login` };
    }
    return <LinkField>{
      link_type: LinkType.Web,
      url: `${this.config.websiteUrl}/${this.lang.currentLanguage()}/auth/login`,
    };
  });
}
